/** @format */
import { ChangeDetectionStrategy, Component, computed, inject, input, InputSignal, Signal, ViewEncapsulation } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CreditRange, StudyPeriodLocator, StudyYear } from 'common-typescript/types';
import moment from 'moment';
import { LocaleService } from 'sis-common';
import { AppErrorHandler } from 'sis-components';
import { TimelineStudyYearsService } from '../../../timeline-study-years.service';
import {
    PlannedStudyTerm,
    timelineMultiPeriodTimingDialogOpener,
} from '../timeline-multi-period-timing-dialog/timeline-multi-period-timing-dialog.component';

const MIN_VISIBLE_PERIOD_COUNT: number = 2;

@Component({
    selector: 'app-timeline-multi-period-timing-notification-box',
    standalone: false,
    templateUrl: './timeline-multi-period-timing-notification-box.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimelineMultiPeriodTimingNotificationBoxComponent {
    readonly plannedPeriods: InputSignal<StudyPeriodLocator[]> = input.required<StudyPeriodLocator[]>();
    /** Localized name of the timed study. */
    readonly studyName: InputSignal<string> = input.required<string>();
    readonly credits: InputSignal<CreditRange> = input.required<CreditRange>();

    private readonly timelineStudyYearsService = inject(TimelineStudyYearsService);
    private readonly localeService = inject(LocaleService);
    private readonly appErrorHandler = inject(AppErrorHandler);
    private readonly openDialog = timelineMultiPeriodTimingDialogOpener();

    private readonly studyYears: Signal<readonly StudyYear[]> = toSignal(
        this.timelineStudyYearsService.studyYears$.pipe(this.appErrorHandler.defaultErrorHandler()),
    );

    /**
     * Study terms having planned periods, in timeline order.
     * Planned periods that are not on the timeline or have no localized name are left out.
     */
    protected readonly plannedStudyTerms: Signal<readonly PlannedStudyTerm[]> = computed(() => {
        const plannedPeriods = new Set<StudyPeriodLocator>(this.plannedPeriods() ?? []);
        return (this.studyYears() ?? [])
            .flatMap(studyYear => studyYear.studyTerms ?? [])
            .map(studyTerm => ({
                startYear: moment(studyTerm.valid.startDate).year(),
                name: this.localeService.localize(studyTerm.name),
                periodNames: (studyTerm.studyPeriods ?? [])
                    .filter(studyPeriod => plannedPeriods.has(studyPeriod.locator))
                    .map(studyPeriod => this.localeService.localize(studyPeriod.name))
                    .filter(periodName => !!periodName),
            }))
            .filter(studyTerm => studyTerm.periodNames.length > 0);
    });

    /**
     * Number of planned periods listed in the dialog, or null if there are less than two of them.
     */
    protected readonly plannedPeriodCount: Signal<number | null> = computed(() => {
        const count = this.plannedStudyTerms().reduce((sum, studyTerm) => sum + studyTerm.periodNames.length, 0);
        return count >= MIN_VISIBLE_PERIOD_COUNT ? count : null;
    });

    protected openPlannedPeriodsDialog(): void {
        this.openDialog({ studyName: this.studyName(), credits: this.credits(), studyTerms: this.plannedStudyTerms() });
    }
}
